import React, { useEffect, useMemo, useRef, useState } from "react";
import PanelContainer from "../common/components/panel-container/panel";
import "./file-management.scss";
import {
  NormalText,
  PageSubHeader,
} from "../common/components/title-container/LText";
import {
  TrashIcon,
  ImportIcon,
  Checkbox,
  ShareIcon,
  PlusIcon,
  FileUpload,
  ProgressIndicator,
  Input,
  RefreshIcon,
  ArrowLeftIcon,
  Card,
  CloseIcon,
  Tooltip,
} from "@forge/common";
import DividerComponent from "../common/components/divider/divider-component";
import TableContainer from "../common/components/table-container/tableContainer";
import {
  FileManagement,
  ICustomizeViewColumns,
} from "./models/file-management.model";
import { useDispatch, useSelector } from "react-redux";
import { hideModel, showLoader, showModel } from "../store/model/model.action";
import { UploadFileManagement } from "./popup/file-management-popup";
import { FILE_MANAGEMENT_CONSTANTS } from "./utils/constant";
import {
  calculatePercentage,
  cancelUploadProgress,
  checkAllCheckbox,
  checkSingleCheckbox,
  clearFilter,
  createNewFolder,
  deleteActionList,
  downloadActionList,
  fetchFileList,
  getAllFiles,
  searchServiceCall,
  searchText,
  sortingOrder,
  updateFileName,
  updateFolderName,
  uploadFile,
  downloadStartedCall,
} from "./store/file-management.action";
import { config } from "../../../../common/authentication/config";
import ButtonComponent from "../common/components/button/button";
import { BodyText } from "../common/components/title-container/LText";
import { sha256HashFile } from "../../../../common/utility/shaHash";
import { dateFormatFun, rendererFileSizeCol, verifyFile } from "./utils/helper";
import ProgressBarComponent from "../common/components/progress-bar/progressbar";
import { btnResponsive } from "../common/utils/helper";
import useAuth from "../../../../common/hooks/useAuth";
import { PermissionConstant } from "../common/utils/permission";
import { resetAbortController } from "../common/interceptor/base.services";
import { showToast } from "../store/model/model.action";

const chunkSize = 5 * 1024 * 1024;
const siteIdStatic = config?.SITE_ID;

interface FileItem {
  status: string;
  updatedBy: string;
  fileName: string;
}

const FileManagementScreen = () => {
  const { hasPermission } = useAuth();
  const dispatch = useDispatch();
  const fileUploader = useRef<any>(null);
  const [finalList, setFinalList] = useState<any>([]);
  const {
    fileList,
    searchValue,
    userDetails,
    storageDetails,
    uploadPercentage,
    rowKey,
    downloadStarted,
  } = useSelector((state: any) => state?.fileManagementReducer);
  const { userInfo } = useAuth();

  const uploadCancelStatus = useRef<boolean>(false);
  const [searchError, setSearchError] = useState<boolean>(false);

  useEffect(() => {
    dispatch(showLoader());
    let payload = {
      siteId: siteIdStatic,
      parentPath: "",
    };
    localStorage.setItem("activeTab", "fileManagement");
    dispatch(fetchFileList(payload));
  }, []);

  const fetchListApi = (path?: string | null) => {
    let payload = {
      // siteId: siteIdStatic,
      parentPath: path || "",
    };
    dispatch(getAllFiles(payload));
  };

  const statusRender = (value: any) => {
    switch (value) {
      case "FILE_UPLOAD_FAILED":
        return "File Upload Failed";
      case "FILE_UPLOAD_INPROGRESS":
        return "File Upload Inprogress";
      default:
        return value;
    }
  };

  const closeUploadProgress = () => {
    uploadCancelStatus.current = true;
    dispatch(calculatePercentage(0, "File download in-progress", 0));
    let payload = {
      siteId: siteIdStatic,
      parentPath: [...finalList]?.join("/"),
    };
    if (fileUploader.current) {
      fileUploader.current.value = "";
    }
    dispatch(cancelUploadProgress(payload));
  };

  const SubTitleContent = () => {
    return (
      <div className="flex justify-between xl-high:mt-2">
        <div>
          <PageSubHeader
            title={FILE_MANAGEMENT_CONSTANTS.LIST_OF_FILES}
            subheading={`${fileList?.length} total`}
          />
        </div>
        <div className="progress-indicator">
          {uploadPercentage?.percentage > 0 && (
            <div>
              <div className="flex justify-between gap-2">
                <BodyText
                  title={`${uploadPercentage?.fileName} (${Math.round(
                    uploadPercentage?.percentage,
                  )}%)`}
                />
                {uploadPercentage?.status ? (
                  <CloseIcon
                    data-testid="file-management-close-icon"
                    className="cursor-pointer"
                    onClick={() => closeUploadProgress()}
                  />
                ) : null}
              </div>
              <ProgressIndicator
                id="progress-bar"
                data-testid="progress-bar"
                fileName={uploadPercentage?.fileName}
                fileSize={100}
                size="regular"
                uploaded={uploadPercentage?.percentage}
                className={`w-[320px] xl-high:w-[420px] xl-high:mt-2`}
              />
            </div>
          )}
        </div>
        <div>
          {storageDetails?.allocatedSize && (
            <ProgressBarComponent
              allocatedSize={storageDetails?.allocatedSize || 0}
              consumedSize={storageDetails?.consumedSize || 0}
            />
          )}
        </div>
      </div>
    );
  };

  const navNewFolder = (data: FileManagement, index: number) => {
    let currentPath = `${finalList.join("/")}`;

    let currFolder;

    if (!data?.containerName) {
      currFolder = data?.fileName;
    } else {
      const path = data?.path ?? "";
      const regex = new RegExp(`^${currentPath}\\s*`);
      currFolder = path.replace(regex, "");
    }
    let _currTopLevelFolder = currFolder?.split("/").filter(Boolean)[0];
    fetchListApi([...finalList, _currTopLevelFolder]?.join("/"));
    setFinalList((prevList: any[]) => {
      return [...prevList, _currTopLevelFolder];
    });
  };

  function shortenFilename(filename: any) {
    if (filename.length > 20) {
      return filename.slice(0, 17) + "...";
    }
    return filename;
  }

  const DashboardStageChunksUpload = async (files: any) => {
    if (files.length > 0) {
      const _file = files[0];
      if (verifyFile(_file, fileUploader)) {
        dispatch(showLoader());
        const parentPath: any = finalList?.join("/");
        const fileName = _file.name;
        const _totalCount =
          _file.size % chunkSize === 0
            ? _file.size / chunkSize
            : Math.floor(_file.size / chunkSize) + 1;
        const createdFileHash = await sha256HashFile(_file);
        try{
        for (
          let currentChunk = 1;
          currentChunk <= _totalCount;
          currentChunk++
        ) {
          const startOfTheChunk = (currentChunk - 1) * chunkSize;
          const endOfTheChunk = startOfTheChunk + chunkSize;
          const chunk = _file.slice(startOfTheChunk, endOfTheChunk);
          if (uploadCancelStatus.current === true) {
            uploadCancelStatus.current = false;
            resetAbortController();
            break;
          }

          const formData = new FormData();
          let data = new FormData();
          data.append("FormFile", chunk);
          data.append("FileName", fileName);
          data.append("FileSize", _file.size.toString());
          data.append("Path", parentPath);
          data.append("FileUploadType", "largefileupload");
          data.append("BlockNumber", currentChunk.toString());
          data.append("RowKey", rowKey ? rowKey : "");
          data.append("IsReplace", "false");
          data.append("FileHash", createdFileHash);
          data.append("totalBlockNumber", _totalCount.toString());
          await new Promise((resolve, reject) => {
            dispatch(uploadFile(data, parentPath, fileName, resolve, reject));
          });
        }
        } catch {
          // Duplicate file or upload error — loop exits cleanly
        }
        if (fileUploader.current) {
          fileUploader.current.value = "";
        }
      }
    }
  };

  const showAlreadyExistPopup = (description: string) => {
    dispatch(
      showModel({
        childTemplate: () => (
          <UploadFileManagement
            title={""}
            description={description}
            btnText={FILE_MANAGEMENT_CONSTANTS.OK}
            onCancel={() => dispatch(hideModel())}
            onTrigger={() => {
              dispatch(hideModel());
            }}
          />
        ),
      }),
    );
  };

  const addLeading = () => {
    return `break-all overflow-wrap-anywhere xl-high:!leading-[50px] xl-mid:!leading-[25px] `;
  };

  const folderValidation = (data: any) => {
    return Number(data?.fileSize) === 0 && data?.isBlob === false;
  };

  const column = [
    {
      key: "checkbox",
      name: "checkbox",
      checkbox: true,
      className: "w-10",
      datatestId: "store-checkbox",
      checked:
        fileList?.length > 0
          ? fileList?.every((item: FileManagement) => item.checked)
          : false,
      onChange: (e: { target: { checked: boolean } }) => {
        if (!downloadStarted) {
          dispatch(checkAllCheckbox(e.target.checked));
        }
      },
      render: (data: FileManagement, index: number) => {
        return (
          <Checkbox
            className="cursor-pointer"
            id={`check_${index}`}
            checked={data?.checked}
            disabled={downloadStarted ? true : false}
            onChange={(e) => {
              dispatch(checkSingleCheckbox(index, e.target.checked));
            }}
          />
        );
      },
    },
    {
      key: "fileName",
      name: "FILE NAME",
      className: "w-[190px]",
      resize: true,
      onSort: (direction: "asc" | "desc") => {
        dispatch(sortingOrder(direction, "fileName"));
      },
      render: (data: FileManagement, index: number) => {
        let folderName = "";
        if (
          data?.status === "FILE_UPLOAD_INPROGRESS" &&
          Number(data?.fileSize) === 0
        ) {
          folderName = data?.path
            ? data.path
                .split("/")
                .filter(Boolean)
                [finalList?.length]?.toString()
            : "";
        } else if (data?.fileName !== "") {
          folderName = data?.fileName?.toString() || "";
        } else {
          folderName = data?.path
            ? data.path
                .split("/")
                .filter(Boolean)
                [finalList?.length]?.toString()
            : "";
        }

        return (
          <React.Fragment>
            {data?.textBox === true ? (
              <Input
                id="course-name"
                className="text-sm font-normal"
                data-testid="folder-name-input"
                size="regular"
                value={folderName}
                autoFocus
                maxLength={100}
                onChange={(e) =>
                  dispatch(
                    updateFileName(
                      index,
                      e.target.value
                        ?.toString()
                        .trimStart()
                        .replace(/[^\w]/g, ""),
                    ),
                  )
                }
                onBlur={(e) => {
                  if (e.target.value === "") {
                    showAlreadyExistPopup(
                      FILE_MANAGEMENT_CONSTANTS.FOLDER_NAME_CANNOT_BE_EMPTY,
                    );
                  } else {
                    const checkAvailable = fileList?.filter((i: any) => {
                      let fileName = "";
                      if (i?.fileName !== "") {
                        fileName = i?.fileName?.toString() || "";
                      } else {
                        fileName = i?.path
                          ? i.path
                              .split("/")
                              .filter(Boolean)
                              [finalList?.length]?.toString()
                          : "";
                      }

                      return (
                        fileName.toLowerCase() === e.target.value.toLowerCase()
                      );
                    });
                    if (checkAvailable?.length > 1) {
                      showAlreadyExistPopup(
                        FILE_MANAGEMENT_CONSTANTS.FOLDER_ALREADY_EXIST,
                      );
                    } else {
                      dispatch(updateFolderName());
                    }
                  }
                }}
              />
            ) : (
              <div
                className={`flex w-full overflow-hidden cursor-pointer ${downloadStarted ? "pointer-events-none opacity-25" : "pointer-events-auto"}`}
                data-testid={`folder-name-${index}`}
                onClick={() =>
                  folderValidation(data) && navNewFolder(data, index)
                }
              >
                <BodyText
                  tooltip={folderName}
                  subTooltip={
                    folderValidation(data) ? "" : `${data?.path}/${folderName}`
                  }
                  title={folderName}
                  className={`${
                    folderValidation(data) && "!text-[#1274b7]"
                  } ${addLeading()}`}
                />
              </div>
            )}
          </React.Fragment>
        );
      },
    },
    ...(searchValue?.trim()
      ? [
          {
            key: "path",
            name: "PATH",
            resize: true,
            className: "w-[200px]",
            render: (data: FileManagement) => {
              return (
                <div className="flex w-full overflow-hidden cursor-pointer">
                  <BodyText
                    tooltip={data?.path || ""}
                    title={data?.path || ""}
                    className={`${addLeading()}`}
                  />
                </div>
              );
            },
          },
        ]
      : []),
    {
      key: "fileSize",
      name: "SIZE",
      resize: true,
      className: "w-[80px]",
      onSort: (direction: "asc" | "desc") => {
        dispatch(sortingOrder(direction, "fileSize"));
      },
      render: (data) => {
        return (
          <div className="flex w-full overflow-hidden cursor-pointer">
            <BodyText
              tooltip={rendererFileSizeCol(data?.fileSize)}
              title={rendererFileSizeCol(data?.fileSize)}
              className={`${addLeading()}`}
            />
          </div>
        );
      },
    },
    {
      key: "updatedAt",
      name: "LAST MODIFIED ON",
      resize: true,
      className: "w-[220px]",
      onSort: (direction: "asc" | "desc") => {
        dispatch(sortingOrder(direction, "updatedAt"));
      },
      render: (data: FileManagement) => {
        return (
          <BodyText
            title={dateFormatFun(data.updatedAt)}
            className={`${addLeading()}`}
          />
        );
      },
    },
    {
      key: "updatedBy",
      name: "LAST MODIFIED BY",
      className: "w-[200px]",
      resize: true,
      onSort: (direction: "asc" | "desc") => {
        dispatch(sortingOrder(direction, "updatedBy"));
      },
      render: (data: FileManagement) => {
        return (
          <div className="flex w-full overflow-hidden cursor-pointer">
            <BodyText
              tooltip={data.isBlob === false ? "" : data.updatedBy}
              title={data.isBlob === false ? "" : data.updatedBy}
              className={`${addLeading()}`}
            />
          </div>
        );
      },
    },
    {
      key: "status",
      name: "status",
      className: "w-[180px]",
      onSort: (direction: "asc" | "desc") => {
        dispatch(sortingOrder(direction, "status"));
      },
      render: (data) => {
        return (
          <div className="flex w-full overflow-hidden cursor-pointer">
            <BodyText
              title={statusRender(data?.status)}
              tooltip={statusRender(data?.status)}
              className={`${addLeading()}`}
            />
          </div>
        );
      },
    },
    {
      key: "actions",
      name: "Actions",
      className: "w-[10px]",
      render: (data: FileManagement, index: number) => {
        if (!data) return <></>;
        return (
          <div className="flex w-full align-center items-center overflow-hidden cursor-pointer">
            {Number(data.fileSize) > 0 &&
              data.status !== "FILE_UPLOAD_INPROGRESS" &&
              data.status !== "FILE_UPLOAD_FAILED" && (
                <ImportIcon
                  className={`${btnResponsive("text-base")} cursor-pointer`}
                  color={checkDownloadDisable() ? "#A0A0A0" : "#1792E5"}
                  onClick={(e) =>
                    checkDownloadDisable()
                      ? null
                      : (dispatch(checkSingleCheckbox(index, true)), downList())
                  }
                />
              )}
          </div>
        );
      },
    },
  ] as ICustomizeViewColumns[];

  const deleteList = () => {
    const selectedItems = fileList
      .filter((item: any) => item.checked === true)
      .map((i: any) => {
        return {
          id: i.containerName,
          fileName: i.fileName,
          partitionKey: i.partitionKey,
          path: i.path,
          rowKey: i.rowKey,
          containerName: i.containerName,
        };
      });

    dispatch(
      showModel({
        childTemplate: () => (
          <UploadFileManagement
            title={
              selectedItems?.length > 1
                ? FILE_MANAGEMENT_CONSTANTS.DELETE_FILES
                : FILE_MANAGEMENT_CONSTANTS.DELETE_FILE
            }
            description={
              FILE_MANAGEMENT_CONSTANTS.ARE_YOU_SURE_YOU_WANT_TO_DELETE
            }
            btnText={FILE_MANAGEMENT_CONSTANTS.CANCEL}
            btnText1={FILE_MANAGEMENT_CONSTANTS.YES}
            onCancel={() => dispatch(hideModel())}
            onTrigger={() => {
              let payload = {
                container: selectedItems[0]?.containerName,
                deleteModel: selectedItems,
                updatedBy: userDetails?.email,
                path: finalList?.length > 0 ? finalList.join("/") : null,
              };
              dispatch(deleteActionList(payload));
              dispatch(hideModel());
            }}
          />
        ),
      }),
    );
  };

  const uploadIsonFile = () => {
    dispatch(
      showModel({
        childTemplate: () => (
          <UploadFileManagement
            title={FILE_MANAGEMENT_CONSTANTS.UPLOAD_FILE}
            description={FILE_MANAGEMENT_CONSTANTS.VALID_FILE_SIZE}
            btnText={FILE_MANAGEMENT_CONSTANTS.CANCEL}
            btnText1={FILE_MANAGEMENT_CONSTANTS.CONTINUE}
            onCancel={() => dispatch(hideModel())}
            onTrigger={() => {
              fileUploader.current?.click();
              dispatch(hideModel());
            }}
          />
        ),
      }),
    );
  };

  const filterSearchData = (value: string) => {
    dispatch(searchText(value));
    if (value.trim() === "") {
      setSearchError(false);
      dispatch(clearFilter());
    } else if (/[\\/:*?"<>|{}]/.test(value)) {
      setSearchError(true);
      return;
    } else {
      setSearchError(false);
      dispatch(searchServiceCall(value.trim()));
    }
  };

  const downList = () => {
    dispatch(
      showToast({
        type: "info",
        content: `Preparing your download. You can start other File Management activities once this one completes`,
      }),
    );
    let payload = {
      downloadfilesmodel: fileList
        .filter((i: any) => i.checked)
        .map((i: any) => {
          let folderName = "";
          if (i?.fileName !== "") {
            folderName = i?.fileName?.toString() || "";
          } else {
            folderName = i?.path
              ? i?.path
                  .split("/")
                  .filter(Boolean)
                  [finalList?.length]?.toString()
              : "";
          }
          return {
            fileName: i?.fileName,
            path: i?.path || "",
            folderName: folderName,
          };
        }),
      container: siteIdStatic,
    };
    dispatch(downloadStartedCall(true));
    // dispatch(calculatePercentage(1, "File download in-progress", 0));
    dispatch(downloadActionList(payload));
  };

  const checkDisabled = () => {
    const inProgressFile = fileList?.filter(
      (i: any) => i.checked === true && i.status === "FILE_UPLOAD_INPROGRESS",
    ).length;
    const selectedFile = fileList?.filter(
      (i: any) => i.checked === true,
    ).length;

    if (uploadPercentage?.percentage > 0) {
      return true;
    } else if (inProgressFile > 0) {
      return true;
    } else if (selectedFile === 0) {
      return true;
    } else if (downloadStarted) {
      return true;
    }
    return false;
  };
  const checkDownloadDisable = () => {
    const inProgressFile = fileList?.filter(
      (i: any) => i.checked === true && i.status === "FILE_UPLOAD_INPROGRESS",
    ).length;
    const selectedFile = fileList?.filter(
      (i: any) => i.checked === true,
    ).length;

    if (uploadPercentage?.percentage > 0) {
      return true;
    } else if (inProgressFile > 0) {
      return true;
    } else if (selectedFile > 0) {
      return true;
    } else if (downloadStarted) {
      return true;
    }
    return false;
  };
  const renderSlotRight = () => {
    return (
      <div className="flex gap-4 items-center">
        <div
          className={`${downloadStarted ? "pointer-events-none opacity-25" : "cursor-pointer"} `}
          onClick={() => {
            if (uploadPercentage?.percentage === 0) {
              fetchListApi(finalList?.length > 0 ? finalList.join("/") : null);
            }
          }}
        >
          <RefreshIcon
            className={`${btnResponsive("icons")} ${
              uploadPercentage?.percentage > 0 ? "opacity-25" : "opacity-1"
            } `}
          />
        </div>
        {hasPermission(PermissionConstant.FILE_MANAGEMENT_DELETE, true) && (
          <ButtonComponent
            className={`xl-high:!pl-[4rem]`}
            id="delete-option"
            data-testId="delete-option"
            onClick={deleteList}
            size="regular"
            icon={<TrashIcon className={`${btnResponsive("text-base")}`} />}
            disabled={checkDisabled()}
            title={FILE_MANAGEMENT_CONSTANTS.DELETE}
            variant="secondary"
            tooltip={"Delete the selected items across VMs"}
          />
        )}

        {/* <ButtonComponent
          className={`xl-high:!pl-[4rem]`}
          onClick={downList}
          id="download-button"
          data-testId="download-button"
          size="regular"
          icon={<ImportIcon className={`${btnResponsive("text-base")}`} />}
          iconPosition="left"
          disabled={checkDisabled() || checkDisabledDownlaod()}
          title={FILE_MANAGEMENT_CONSTANTS.DOWNLOAD}
          variant="secondary"
          tooltip={"Download the selected items to this PC"}
        /> */}
        {hasPermission(PermissionConstant.FILE_MANAGEMENT_UPLOAD, true) && (
          <ButtonComponent
            onClick={uploadIsonFile}
            className={`xl-high:!pr-[4rem]`}
            id="upload-button"
            data-testId="upload-button"
            size="regular"
            icon={<ShareIcon className={`${btnResponsive("text-base")}`} />}
            tooltip={`Upload files for all VM' S to Access`}
            iconPosition="right"
            variant="primary"
            disabled={
              uploadPercentage?.percentage > 0 || downloadStarted
                ? true
                : fileList?.filter((i: any) => i.checked).length > 0
            }
            title={FILE_MANAGEMENT_CONSTANTS.UPLOAD_FILES}
          />
        )}
      </div>
    );
  };

  const addNewFolder = () => {
    if (!fileList.find((i: any) => i.textBox === true)) {
      dispatch(createNewFolder());
    }
  };

  const renderSlotLeft = () => {
    return (
      <div className="flex items-center mr-2">
        <ButtonComponent
          data-testid="file-management-addNewFolder"
          className={`xl-high:!pr-[4rem]`}
          onClick={() => addNewFolder()}
          size="regular"
          icon={<PlusIcon className={`${btnResponsive("text-base")}`} />}
          iconPosition="right"
          variant="primary"
          title={FILE_MANAGEMENT_CONSTANTS.NEW_FOLDER}
          tooltip="New Folder"
          disabled={downloadStarted}
        />
      </div>
    );
  };

  const renderRight = () => {
    return (
      <div
        data-testid="back-to-inital"
        className="back-button flex items-center cursor-pointer text-sm font-bold"
        onClick={() => {
          setFinalList([]);
          fetchListApi(null);
        }}
      >
        <ArrowLeftIcon className="cursor-pointer text-sm font-bold text-[#64C3FF]" />
        <NormalText
          title={FILE_MANAGEMENT_CONSTANTS.BACK_TO_FILE_MANAGEMENT}
          className={`cursor-pointer text-sm ${btnResponsive(
            "text-sm",
          )} font-bold text-[#64C3FF] ml-2`}
        />
      </div>
    );
  };

  const filterInProgressBar = useMemo(() => {
    const inProgressFile = fileList?.find((item: FileItem) => {
      return (
        item.status === "FILE_UPLOAD_INPROGRESS" &&
        item.updatedBy === userInfo?.email
      );
    });
    return inProgressFile?.fileName
      ? shortenFilename(inProgressFile.fileName)
      : null;
  }, [fileList, userInfo]);

  const UploadInProgress = ({ name }: { name: string }) => {
    return (
      <div className="flex justify-center gap-2 w-full mb-2 mt-1">
        <div className="flex justify-center gap-2">
          <BodyText
            title={`${name} upload failed. Please cancel to upload again.`}
          />
          <CloseIcon
            data-testid="file-management-close-icon"
            className="cursor-pointer"
            onClick={() => closeUploadProgress()}
          />
        </div>
      </div>
    );
  };
  const inProgressFileName = filterInProgressBar;
  return (
    <PanelContainer
      title={FILE_MANAGEMENT_CONSTANTS.WELCOME_TO_FILE_MANAGEMENT}
      headerRight={finalList?.length > 0 && renderRight()}
      disableNavigation={uploadPercentage?.percentage > 0 || downloadStarted}
    >
      <FileUpload
        ref={fileUploader}
        data-testid="fm-file-upload"
        accept={["text/json"]}
        containerClassName="custom-class hidden"
        description="Accepted file types `text/json` only. File should not exceed 500kb."
        i18nBrowse="Browse files"
        i18nDragDrop="Drag and drop here"
        i18nOr="or"
        id="file-upload"
        onFileUpload={DashboardStageChunksUpload}
        orientation="horizontal"
        variant="drag-drop"
      />
      {inProgressFileName && <UploadInProgress name={inProgressFileName} />}
      {searchError && (
        <div className="error-message">
          <p>{`Search can't contain any of the following characters: / \\ : * ? " < > | { }`}</p>
        </div>
      )}
      <SubTitleContent />
      <div className="mt-2 mb-2">
        <DividerComponent />
      </div>
      <TableContainer
        column={column}
        data={fileList}
        btnText={FILE_MANAGEMENT_CONSTANTS.DELETE_SELECTED}
        hidePageCount
        btnClassName="table-delete-button"
        onClick={deleteList}
        onSearchFunction={(value: string) => {
          filterSearchData(value);
        }}
        downloadStarted={downloadStarted}
        searchValue={searchValue}
        searchPlaceHolder={FILE_MANAGEMENT_CONSTANTS.SEARCH_THE_NAME}
        slotRight={renderSlotRight()}
        slotLeft={renderSlotLeft()}
      />
    </PanelContainer>
  );
};

export default FileManagementScreen;
